import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { createPaymentIntent } from '../api/userApi';
import { Link } from 'react-router-dom';

const FREE_BETA_MODE = true;

const CARD_STYLE = {
  style: {
    base: {
      color: '#fff',
      fontFamily: 'inherit',
      fontSize: '16px',
      '::placeholder': { color: '#9ca3af' },
    },
    invalid: { color: '#ef4444' },
  },
};

const DonationStep = ({ onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [amount, setAmount] = useState('1.50');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleFreeBetaContinue = () => {
    onSuccess({
      paymentIntentId: `FREE_BETA_${Date.now()}`,
      amount: 0,
      isFreeBeta: true,
    });
  };

  const handleSubmit = async () => {
    if (!stripe || !elements) return;

    const parsed = parseFloat(amount);

    if (isNaN(parsed) || parsed < 1.5) {
      setError('Minimum contribution is $1.50');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { clientSecret } = await createPaymentIntent(parsed);

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });

      if (result.error) {
        setError(result.error.message);
        return;
      }

      onSuccess({
        paymentIntentId: `FREE_BETA_${Date.now()}`,
        amount: 0,
        isFreeBeta: true,
      });
    } catch (err) {
      setError('Payment failed. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="donation-step">
      <h2 className="step-title">Request a Service</h2>

      <p className="step-subtitle">
        Need help with your goals? Submit a service request and we’ll help correct your tracking
        history or review your custom request.
      </p>

      <div className="donation-info-box">
        {FREE_BETA_MODE && (
          <p className="donation-info-item">
            🧪 <strong>Beta Access</strong> — Service requests are currently free for a limited time
            while we collect feedback and improve the experience.
          </p>
        )}

        <p className="donation-info-item">
          🎯 <strong>Goal Check</strong> — Missed marking a goal? Submit a request to correct your
          tracking history for a past date, up to 20 days back. Eligible requests are processed
          automatically.
        </p>

        <p className="donation-info-item">
          ⭐ <strong>XP Notice</strong> — Goal checks updated through service requests will not
          award XP. They only correct your tracking history.
        </p>

        <p className="donation-info-item">
          ✏️ <strong>Custom Request</strong> — Need something else? Describe it and our team will
          review it.
        </p>

        {!FREE_BETA_MODE && (
          <p className="donation-info-item">
            💳 <strong>Non-Refundable</strong> — Contributions are non-refundable once a request is
            submitted. You’re supporting the service and review process.
          </p>
        )}
      </div>

      {FREE_BETA_MODE ? (
        <button className="donate-btn" onClick={handleFreeBetaContinue}>
          Submit Free Request
        </button>
      ) : (
        <>
          <div className="amount-input-wrapper">
            <span className="dollar-sign">$</span>
            <input
              type="number"
              min="1.50"
              step="0.50"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="amount-input"
              placeholder="1.50"
            />
          </div>

          <div className="card-element-wrapper">
            <CardElement options={CARD_STYLE} />
          </div>

          {error && <p className="donation-error">{error}</p>}

          <button className="donate-btn" onClick={handleSubmit} disabled={loading || !stripe}>
            {loading ? 'Processing...' : `Continue with $${amount}`}
          </button>
        </>
      )}

      <div
        className="view-history-footer"
        style={{
          marginTop: '24px',
          textAlign: 'center',
          borderTop: '1px solid #f1f5f9',
          paddingTop: '16px',
        }}
      >
        <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '8px' }}>
          Already submitted a request?
        </p>

        <Link
          to="/service-request/history"
          style={{
            fontSize: '0.9rem',
            color: 'var(--text-main)',
            fontWeight: '600',
            textDecoration: 'underline',
          }}
        >
          Check your Request Status & History
        </Link>
      </div>
    </div>
  );
};

export default DonationStep;
